import React from 'react'
import { connect } from 'react-redux'
import { reduxForm, Field, formValueSelector } from 'redux-form'
import { withRouter } from 'react-router-dom'

import { DATABASE_TYPE_OPTIONS } from '../../../utils/databases'
import Card from '../../Shared/Card'
import Button from '../../Shared/Button'
import WrappedInput from '../../Shared/Forms/WrappedInput'
import FormError from '../../Shared/Forms/FormError'
import Firestore from './Firestore'
import Airtable, { API_KEY_PATTERN } from './Airtable'
import Stripe from './Stripe'
import MongoDB from './MongoDB'

const FORM_NAME = 'newDatabase'

const validate = values => {
  let errors = {}

  if (!values.name) {
    errors.name = 'Please set the display name for your database'
  }

  if (!values.type) {
    errors.type = 'Please choose a source type'
  }

  if (!values.host) {
    errors.host = 'Please add your database host'
  }

  if (!values.port) {
    errors.port = 'Please add your database port'
  } else if (
    !values.port.match(/^\d+$/) ||
    +values.port < 1 ||
    +values.port > 65535
  ) {
    errors.port = `The port must be a number between 1 and 65535`
  }

  if (!values.username) {
    errors.username = 'Please add your database username'
  }

  if (!values.password) {
    errors.password = 'Please add your database password'
  }

  if (['postgres', 'mysql'].includes(values.type) && !values.dbName) {
    errors.dbName = 'Please add your database name'
  }

  if (!values.serviceAccountKey) {
    errors.serviceAccountKey = 'Please upload your Firebase Service Account Key'
  }

  if (values.type === 'airtable') {
    if (!values.apiKey) {
      errors.apiKey = 'Please add your Airtable API key'
    } else if (!values.apiKey.match(API_KEY_PATTERN)) {
      errors.apiKey = 'This doesn’t appear to be a valid API key'
    }
  } else if (values.type === 'stripe') {
    if (!values.apiKey) {
      errors.apiKey = 'Please add your Stripe Secret Key'
    } else if (values.apiKey.match(/^pk_/)) {
      errors.apiKey = 'This appears to be a publishable key, not a secret key'
    } else if (!values.apiKey.match(/^sk_(test|live)_/)) {
      errors.apiKey = 'This doesn’t appear to be a valid API key'
    }
  }

  if (!values.baseId) {
    errors.baseId = 'Please select the base you’d like to use'
  }

  return errors
}

const Form = function NewDatabaseForm({
  handleSubmit,
  submitting,
  history,
  type,
  error,
  title,
  subtitle,
  hideType,
  onCancel,
  cancelButtonText,
}) {
  return (
    <Card
      component="form"
      onSubmit={handleSubmit}
      actions={
        <>
          <Button text type="button" onClick={onCancel || history.goBack}>
            {cancelButtonText || 'Cancel'}
          </Button>
          <Button loading={submitting}>
            {submitting ? 'Checking Connection...' : 'Save'}
          </Button>
        </>
      }
    >
      {title && <h1>{title}</h1>}
      {subtitle && <p className="subtitle">{subtitle}</p>}
      {!hideType && (
        <Field
          component={WrappedInput}
          name="type"
          label="Database Type"
          placeholder="Select..."
          options={DATABASE_TYPE_OPTIONS.concat([
            {
              label: 'More coming soon — please email us',
              value: 'more',
              disabled: true,
            },
          ])}
        />
      )}
      {['mysql', 'postgres'].includes(type) && (
        <>
          <Field
            component={WrappedInput}
            name="host"
            label="Host"
            placeholder={`${type}.myserver.com`}
            autoComplete="off"
          />
          <Field
            component={WrappedInput}
            name="port"
            label="Port"
            placeholder={type === 'mysql' ? 3306 : 5432}
            autoComplete="off"
            type="number"
          />
          <Field
            component={WrappedInput}
            name="username"
            label="Username"
            placeholder="root"
            autoComplete="off"
          />
          <Field
            component={WrappedInput}
            name="password"
            label="Password"
            placeholder="••••••••"
            autoComplete="off"
            type="password"
          />
          <Field
            component={WrappedInput}
            name="dbName"
            label="Database Name"
            autoComplete="off"
            placeholder="my_db"
          />
          <Field
            component={WrappedInput}
            name="name"
            label="Nickname"
            placeholder="My New Database"
            autoComplete="off"
          />
        </>
      )}
      {type === 'firestore' && <Firestore form={FORM_NAME} />}
      {type === 'airtable' && <Airtable form={FORM_NAME} />}
      {type === 'stripe' && <Stripe form={FORM_NAME} />}
      {type === 'mongo' && <MongoDB />}
      {error && <FormError>{error}</FormError>}
    </Card>
  )
}

const selector = formValueSelector(FORM_NAME)

const mapStateToProps = state => ({
  type: selector(state, 'type'),
})

export default connect(mapStateToProps)(
  withRouter(
    reduxForm({
      form: FORM_NAME,
      validate,
    })(Form)
  )
)
